import postMessageToWindow from '@/api/postMessageToWindow';
import rootAPIs from '@/api/rootAPIs';
import SegmentMixin from '@/mixins/SegmentMixin';
import { mapGetters, mapMutations, mapState } from 'vuex';

/**
 * Form Mixin
 *
 * Contains general methods, props, and data for forms to use.
 */
export default {
  mixins: [
    SegmentMixin,
  ],
  props: {
    /**
     * The ID for the application this form is related to. Used to fetch
     * the form information and for submitting.
     */
    applicationId: String,
    /**
     * Parameter to skip tracking if known bot
     */
    isKnownBot: String,
    /**
     * Segment anonymous id for tracking events
     */
    anonymousId: String,
    /**
     * The ID for the cache entry for a given customer to retrieve validation
     * data.
     */
    customerId: String,
    /**
     * Should form be for a full screen or for modal.
     */
    fullScreen: Boolean,
    /**
     * Parameter to check if OCR flow enabled
     */
    ocrEnabled: String,
  },
  data() {
    return {
      // The Object that stores all of the field values
      formSubmitFields: {},
      // The Array of form field configurations
      formFields: [],
      // Is the form being submitted at the moment
      inSubmission: false,
      nonFieldErrors: [],
      formObject: {},
    };
  },
  methods: {
    ...mapGetters('forms', [
      'getCurrentFormObject',
      'getCurrentFormIndex',
      'getCurrentForms',
      'getCurrentForm',
    ]),
    ...mapMutations('forms', [
      'setCurrentFormIndex',
      'incrementFormIndex',
      'incrementVerificationAttempts',
    ]),
    /**
     * Adds the errors given to the ErrorBag
     * @param errors {Object} An object of errors to be added
     */
    handleError(errors) {
      Object.keys(errors).forEach((key) => {
        let error = errors[key];

        if (key === 'non_field_errors') {
          // Generic non field errors
          this.nonFieldErrors = [error];
        } else {
          if (error.non_field_errors) {
            // Complex field non field errors
            ([error] = error.non_field_errors);
          }
          this.errors.add({ field: key, msg: error });
        }
      });
      this.inSubmission = false;
      postMessageToWindow({
        close: false,
        reason: 'formError',
        data: {
          errors,
        },
      });
    },
    /**
     * Submit the form data and handle the response from the backend
     * @param type {String} The type of the form that is being submitted
     */
    submitFormData(type) {
      this.inSubmission = true;
      this.nonFieldErrors = [];
      this.formSubmitFields.known_bot = this.isKnownBot;
      this.formSubmitFields.anonymous_id = this.anonymousId;
      this.formSubmitFields.validation_bypass_code = this.validationBypassCode;
      this.formSubmitFields.utility_id = this.forms[this.getCurrentFormIndex()].utility_id;
      this.formSubmitFields.tracking_cart_id = this.trackingCartId;
      this.trackCurrentFormEvent(this.segment.constants.events.SUBMITTED);
      return rootAPIs.submitFormData(
        type,
        this.applicationId,
        this.formSubmitFields,
        this.formObject.id,
      ).then((data) => {
        this.$store.commit({
          type: 'setHasShippingData',
          hasShippingData: data.has_shipping_data,
        });
        if (!data.utility_verification) {
          this.handleError(data.utility_verification_errors);
          this.incrementVerificationAttempts();
          return;
        }

        const nextForm = this.getCurrentForms()[this.getCurrentFormIndex() + 1] || {};
        if (nextForm.type) {
          this.$store.commit('forms/incrementFormIndex');
        }

        if (type === 'verification') {
          const additionalVerificationTriggered = this.verificationAttempts >= 1;
          if (additionalVerificationTriggered) {
            // Track additional verification event
            const EVENT = 'Secondary Verification Success';
            const properties = {
              additionalVerificationUsed: this.additionalVerificationUsed,
            };
            this.trackEvent(EVENT, properties);
          }
        }

        if (nextForm.type === 'reporting' && data.utility_verification) {
          this.$router.push({
            name: 'reporting-form',
            params: {
              applicationId: this.applicationId,
              index: this.$store.getters['forms/getCurrentFormIndex'],
            },
            query: {
              bot: this.isKnownBot,
              suid: this.anonymousId,
              fullScreen: this.fullScreen,
              ocrEnabled: this.ocrEnabled,
            },
          });
        } else {
          // Bypass code has been burned, the stored code is removed.
          this.$store.commit('customerData/removeValidationBypassCode');
          const callback = () => postMessageToWindow({
            close: true,
            reason: 'formComplete',
            data,
          });
          this.trackEvent('Validation Flow Complete', {}, callback);
          this.inSubmission = false;
        }
      }, (error) => {
        if (error.response.status >= 500) {
          this.$router.push({ name: 'FiveZeroZero' });
        } else {
          this.handleError(error.response.data);
        }
      });
    },
    /**
     * Retrieve the form field configuration for the given type.
     * @param type {String} The form type
     * @return {AxiosPromise} A promise with either the form data or an error
     */
    getFormsFields(type) {
      return rootAPIs.getFormFields(this.applicationId, type)
        .then((response) => {
          this.formFields = response.data.fields;
        })
        .catch(() => {
          this.$router.push({ name: 'FiveZeroZero' });
        });
    },

    /**
     * Gets the current form from the store
     * and initializes the formObject and formFields
     * on the component.
     */
    initializeForm() {
      const form = this.getCurrentFormObject();
      this.formObject = form;
      this.formFields = form.fields;
    },

    isLastForm() {
      // Check if current form is the last form
      return this.getCurrentFormIndex() === (this.getCurrentForms().length - 1);
    },

    /**
     * Remove non-field errors from the error bag and reset the
     * nonFieldErrors array to empty.
     */
    filterOutNonFieldErrors() {
      this.nonFieldErrors = [];
      // Removes all errors with no id property.  Since field errors do
      // have an id, this should remove all non-field errors.
      this.errors.removeById();
    },
  },
  computed: {
    ...mapState('customerData', ['validationBypassCode', 'trackingCartId']),
    ...mapState('forms', ['verificationAttempts']),
    ...mapGetters('cmsText', ['getCMSText']),
    ...mapState('siteSettings', ['settings']),
    /**
     * Evaluates to true if the form is invalid, and false otherwise.
     * @return {boolean} Whether or not the submit button should be disabled
     */
    sanitizedDescription() {
      // Return the form description, sanitized, if any.
      return this.$sanitize(this.formObject.description || '');
    },
    /**
     * Creates an array of keys for each additional verification field that was used.
     * @return {String[]} Array of field keys
     */
    additionalVerificationUsed() {
      return this.formFields
        .filter(field => field.additional_verification && this.formSubmitFields[field.key])
        .map(field => field.key);
    },
  },
  created() {
    // Clear the error bag when creating the form so that previous errors don't cause issues
    this.errors.clear();
  },
};
